/**
 * Live-book helpers for Underwriting, Surveillance and Valuation.
 *
 * Demo modules may keep the curated Antarctica dataset. Live modules must
 * render the pipeline the API actually returned, or a truthful empty state —
 * never fabricated deals, appraisals or CRM rows.
 */
import {
  LOAN_STAGES,
  type CashFlowData,
  type Loan,
  type LoanStage,
  type MarketData,
  type Trigger,
  type ValuationRecord,
} from '@/types'

/**
 * Live underwriting is the working pipeline, not a late-stage subset.
 *
 * Restricting the book to Term Sheet Signed → Funded hid every production
 * deal while the live partition sat entirely in Triage (verified against
 * GET /loans/ on cre.helios-fintech.com). Demo still requires stress tests.
 */
export const UW_STAGES: LoanStage[] = [...LOAN_STAGES]

export function isUnderwritingEligible(loan: Loan, requireStressTests = false): boolean {
  if (!UW_STAGES.includes(loan.stage)) return false
  if (requireStressTests) return Boolean(loan.stressTests && loan.stressTests.length > 0)
  return true
}

export function underwritingBook(loans: Loan[], opts: { live: boolean }): Loan[] {
  return loans.filter((loan) => isUnderwritingEligible(loan, !opts.live))
}

export function isFundedLoan(loan: Loan): boolean {
  return loan.stage === 'Funded'
}

/**
 * Surveillance prefers funded positions. When the live book has none yet,
 * show the pipeline that is on file rather than an empty wall.
 */
export function surveillanceBook(loans: Loan[]): Loan[] {
  const funded = loans.filter(isFundedLoan)
  return funded.length > 0 ? funded : loans
}

/** A number the API actually sent. Null, NaN and non-numbers are absent. */
export function asFinite(value: number | null | undefined, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

/** The unit a loan's amounts are recorded in: dollars unless the book says millions. */
export type AmountUnit = 'USD' | 'USD_MM'

export function amountUnitOf(loan: Pick<Loan, 'amountUnit'> | null | undefined): AmountUnit {
  return loan?.amountUnit === 'USD_MM' ? 'USD_MM' : 'USD'
}

/**
 * An amount in $MM. The live loans API stores DOLLARS; only the curated demo
 * book is kept in $MM, and it declares so (`Loan.amountUnit`).
 *
 * This used to guess the unit from the size — anything under 1,000 was taken
 * to be $MM already — so a live $850 figure read as $850M. That is the rule
 * package M removed from every report and board (CMP-08): a size is never
 * guessed from its magnitude; only a declared millions unit is left as-is.
 */
export function toMillions(value: number | null | undefined, unit: AmountUnit = 'USD'): number {
  const n = asFinite(value)
  return unit === 'USD_MM' ? n : n / 1_000_000
}

const EMPTY_CASH_FLOWS: CashFlowData = {
  historicNOI: 0,
  prospectiveNOI: 0,
  variability: 'Medium',
  expenseRatio: 0,
  revenue: 0,
  expenses: 0,
  rents: 0,
  leaseVelocity: 'Moderate',
}

const EMPTY_MARKET: MarketData = {
  msa: '',
  vacRate: 0,
  rentGrowth: 0,
  capRate: 0,
  absorption: 0,
}

/**
 * Cash flows in $MM. Intake and the underwriting-model seed store
 * `grossPotentialRent` / `effectiveGrossIncome` and an ARRAY of expense lines;
 * this read only `rents` / `revenue` / a numeric `expenses`, so the control
 * deal's operating statement showed $0.00M everywhere but NOI (MOD-09).
 */
export function cashFlowsOf(loan: Loan): CashFlowData {
  const cf = loan.cashFlows
  if (!cf || typeof cf !== 'object') return EMPTY_CASH_FLOWS
  const raw = cf as unknown as Record<string, unknown>
  const expenseLines = Array.isArray(raw.expenses) ? (raw.expenses as Array<Record<string, unknown>>) : null
  const expensesTotal = expenseLines
    ? expenseLines.reduce((sum, line) => sum + asFinite(line?.amount as number), 0)
    : asFinite(raw.expenses as number)
  const unit = amountUnitOf(loan)
  return {
    historicNOI: toMillions(cf.historicNOI, unit),
    prospectiveNOI: toMillions(cf.prospectiveNOI, unit),
    variability: cf.variability ?? EMPTY_CASH_FLOWS.variability,
    expenseRatio: asFinite(cf.expenseRatio),
    revenue: toMillions(asFinite(raw.revenue as number) || asFinite(raw.effectiveGrossIncome as number), unit),
    expenses: toMillions(expensesTotal, unit),
    rents: toMillions(asFinite(raw.rents as number) || asFinite(raw.grossPotentialRent as number), unit),
    leaseVelocity: cf.leaseVelocity ?? EMPTY_CASH_FLOWS.leaseVelocity,
    extraordinaryCapital:
      cf.extraordinaryCapital != null ? toMillions(cf.extraordinaryCapital, unit) : undefined,
  }
}

export function marketOf(loan: Loan): MarketData {
  const market = loan.market
  if (!market || typeof market !== 'object') return EMPTY_MARKET
  return {
    msa: market.msa ?? '',
    vacRate: asFinite(market.vacRate),
    rentGrowth: asFinite(market.rentGrowth),
    capRate: asFinite(market.capRate),
    absorption: asFinite(market.absorption),
  }
}

export type CovenantStatus = 'Compliant' | 'Watch' | 'Breach' | 'Unknown'
export type OverallStatus = 'Performing' | 'Watch' | 'Distressed' | 'Unknown'

export interface WatchlistRow {
  deal: Loan
  currentDscr: number | null
  currentLtv: number | null
  dscrTrend: 'up' | 'down' | 'flat'
  covenantStatus: CovenantStatus
  overallStatus: OverallStatus
}

/**
 * Watchlist rows from the loans the caller already selected.
 *
 * Live: there is no post-close snapshot and no covenant test on the loans
 * payload. The rows used to copy the ORIGINATION DSCR/LTV into the "current"
 * columns and grade them Breach/Watch/Compliant against fixed 1.20x/1.25x/
 * 80%/75% thresholds that are not the loan's covenants, so Portfolio Health
 * read 50% and Monitoring disagreed on the same loan (MOD-24). Current
 * metrics are now absent and the covenant status is Unknown until a covenant
 * test is recorded; the origination figures stay in their own columns.
 */
export function watchlistFromLoans(loans: Loan[], opts?: { demoDrift?: boolean }): WatchlistRow[] {
  return loans.map((deal) => {
    if (opts?.demoDrift) {
      return demoWatchlistRow(deal)
    }
    return {
      deal,
      currentDscr: null,
      currentLtv: null,
      dscrTrend: 'flat' as const,
      covenantStatus: 'Unknown' as const,
      overallStatus: 'Unknown' as const,
    }
  })
}

function demoWatchlistRow(deal: Loan): WatchlistRow {
  const isStressed = deal.id === 'ALG-1018'
  const isMezz = deal.id === 'ALG-1017'
  const currentDscr = isStressed ? 1.08 : isMezz ? 1.14 : +(asFinite(deal.dscr) + 0.02).toFixed(2)
  const currentLtv = isStressed ? 0.84 : isMezz ? 0.87 : +(asFinite(deal.ltv) - 0.01).toFixed(2)
  const dscrTrend: WatchlistRow['dscrTrend'] = currentDscr >= asFinite(deal.dscr) ? 'up' : 'down'
  const covenantStatus: CovenantStatus = isStressed ? 'Breach' : isMezz ? 'Watch' : 'Compliant'
  const overallStatus: OverallStatus = isStressed ? 'Distressed' : isMezz ? 'Watch' : 'Performing'
  return { deal, currentDscr, currentLtv, dscrTrend, covenantStatus, overallStatus }
}

export interface SurveillanceEvent {
  id: string
  description: string
  dealRef: string
  property: string
  due: string
  severity: 'high' | 'medium' | 'low'
  status: 'overdue' | 'pending' | 'upcoming'
}

export function eventsFromLoanTriggers(loans: Loan[]): SurveillanceEvent[] {
  const events: SurveillanceEvent[] = []
  for (const deal of loans) {
    const triggers = Array.isArray(deal.triggers) ? deal.triggers : []
    for (const trigger of triggers) {
      events.push(eventFromTrigger(deal, trigger))
    }
  }
  return events.sort((a, b) => {
    const sevOrder = { high: 0, medium: 1, low: 2 }
    if (sevOrder[a.severity] !== sevOrder[b.severity]) return sevOrder[a.severity] - sevOrder[b.severity]
    return new Date(a.due).getTime() - new Date(b.due).getTime()
  })
}

function eventFromTrigger(deal: Loan, trigger: Trigger): SurveillanceEvent {
  const status =
    trigger.status === 'overdue' || trigger.status === 'pending' || trigger.status === 'upcoming'
      ? trigger.status
      : 'pending'
  return {
    id: trigger.id,
    description: trigger.description || trigger.type,
    dealRef: deal.id,
    property: deal.property,
    due: trigger.due,
    severity: trigger.severity,
    status,
  }
}

/**
 * A valuation queue row from a live loan. Only fields the loan already
 * carries are filled; missing appraisal inputs stay absent rather than
 * being invented.
 */
export function deriveValuationRecords(loans: Loan[]): ValuationRecord[] {
  const records: ValuationRecord[] = []
  for (const loan of loans) {
    const record = valuationFromLoan(loan)
    if (record) records.push(record)
  }
  return records
}

function valuationFromLoan(loan: Loan): ValuationRecord | null {
  // Value and NOI as the record holds them: a live loan carries its value in
  // market_data.appraisedValue / custom property_value, never on the loan
  // itself, which is why every live row read $0.0M (MOD-10).
  const reading = propertyValueOf(loan)
  const valueDollars = reading?.value ?? null
  const noiDollars = recordedNoiOf(loan)
  if (valueDollars == null && noiDollars == null) return null

  const unit = amountUnitOf(loan)
  const asIsValue = valueDollars != null ? toMillions(valueDollars, unit) : 0
  const noi = noiDollars != null ? toMillions(noiDollars, unit) : 0
  // A PERCENT. Derived from NOI ÷ value when both exist; else the stored cap
  // rate, normalised (intake stores 0.0703, the seed 5.15).
  const capRate = asIsValue > 0 && noi > 0 ? (noi / asIsValue) * 100 : capRatePercent(loan.market?.capRate) ?? 0
  const occupancy = recordedOccupancyOf(loan) ?? 0

  return {
    propertyId: loan.id,
    propertyName: loan.property,
    valuationDate: loan.originated || loan.createdAt || '',
    appraiser: '',
    asIsValue,
    // No stabilized value, discount or terminal rate is on record: 0 renders
    // as "—". It used to repeat the as-is value as the stabilized one.
    stabilizedValue: 0,
    capRate,
    discountRate: 0,
    terminalCapRate: 0,
    noi,
    occupancy,
    // Nothing has been ordered or drafted; "Awaiting Draft" claimed otherwise.
    status: 'No Appraisal',
    valuationType: loan.stage === 'Funded' ? 'post-close' : 'pre-deal',
    dealId: loan.id,
    ...(reading?.state === 'derived' ? { valueBasis: `Derived: ${reading.formula}` } : {}),
  }
}

/** A positive finite number, else null. Zero is "not recorded" for these fields. */
function positive(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null
}

/**
 * A custom field by its stored snake_case name. The loans service camel-cases
 * nested keys too, so `property_value` arrives as `propertyValue`.
 */
function customNumber(loan: Loan, key: string): number | null {
  const fields = loan.customFields
  if (!fields || typeof fields !== 'object') return null
  const rec = fields as Record<string, unknown>
  const camel = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  return positive(key in rec ? rec[key] : rec[camel])
}

function marketNumber(loan: Loan, key: string): number | null {
  const market = loan.market as unknown
  if (!market || typeof market !== 'object') return null
  return positive((market as Record<string, unknown>)[key])
}

function cashFlowNumber(loan: Loan, key: string): number | null {
  const cf = loan.cashFlows as unknown
  if (!cf || typeof cf !== 'object') return null
  return positive((cf as Record<string, unknown>)[key])
}

/**
 * The property value the deal record holds, in DOLLARS, or null.
 *
 * Live loans never carry a top-level `propertyValue`: intake writes the value
 * to `market_data.appraisedValue` and `custom_fields.property_value`. Reading
 * only `loan.propertyValue` is why Valuation showed The Marlowe's as-is value
 * as $0.0M while the record holds $37.7M.
 *
 * A `property_value` the platform DERIVED is not a recorded value. No rule
 * derives one any more (R3 retired loan ÷ LTV and NOI ÷ cap), so a derived
 * one is a back-solve the reconciliation sweep has yet to withdraw — Senate
 * Row's $108,012,820.51 is $67.4M ÷ 62.4%, and Valuation and Screening
 * presented it as what the deal records. The loans API names it in
 * `derived_fields`.
 */
export function recordedValueOf(loan: Loan): number | null {
  const derived = (loan.derivedFields ?? []).includes('property_value')
  return (
    positive(loan.propertyValue) ??
    marketNumber(loan, 'appraisedValue') ??
    (derived ? null : customNumber(loan, 'property_value'))
  )
}

/** The formula of a derived value a current computation made, or null. */
export function derivedFormulaOf(loan: Loan, field: string): string | null {
  const formulas = loan.derivedFormulas
  if (!formulas || typeof formulas !== 'object') return null
  const camel = field.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
  const formula = formulas[field] ?? formulas[camel]
  return typeof formula === 'string' && formula ? formula : null
}

export interface PropertyValueReading {
  /** DOLLARS (or the deal's own amount unit). */
  value: number
  state: 'recorded' | 'derived'
  /** The formula of a derived value; null for a recorded one. */
  formula: string | null
}

/**
 * The deal's property value and how it was arrived at, or null.
 *
 * A recorded value first. Otherwise a value the platform DERIVED under a
 * current computation — a component valuation a person configured (45 Park
 * Ave: sum of NOI / cap per component) — labelled Derived with its formula.
 * Dropping that one took 45 Park off the Valuation queue and made Screening
 * say the deal "records no property value" (nd-017). A back-solve a retired
 * rule left names no current formula and is still never read as a value.
 */
export function propertyValueOf(loan: Loan): PropertyValueReading | null {
  const recorded = recordedValueOf(loan)
  if (recorded != null) return { value: recorded, state: 'recorded', formula: null }
  if (!(loan.derivedFields ?? []).includes('property_value')) return null
  const formula = derivedFormulaOf(loan, 'property_value')
  const derived = formula ? customNumber(loan, 'property_value') : null
  return derived != null ? { value: derived, state: 'derived', formula } : null
}

/** In-place NOI on record, in DOLLARS, or null. */
export function recordedNoiOf(loan: Loan): number | null {
  return positive(loan.noi) ?? cashFlowNumber(loan, 'prospectiveNOI') ?? customNumber(loan, 'noi')
}

/**
 * Occupancy on record as a FRACTION (0.94), or null.
 *
 * Stored three ways: `cash_flows.occupancy` as a fraction (0.94) or a percent
 * (94.23), `custom_fields.occupancy` as a percent, and — for curated deals —
 * `market_data.vacRate` as a percent. Valuation read only the last, so every
 * intake deal showed 0% occupied.
 */
export function recordedOccupancyOf(loan: Loan): number | null {
  const stated = cashFlowNumber(loan, 'occupancy') ?? customNumber(loan, 'occupancy')
  if (stated != null) return stated > 1 ? stated / 100 : stated
  const vacancy = marketNumber(loan, 'vacRate')
  if (vacancy != null && vacancy <= 100) return 1 - vacancy / 100
  return null
}

/**
 * A cap rate as a PERCENT (7.03), whichever unit it was stored in.
 *
 * Intake stores fractions (0.0703); the curated seed stores percents (5.15).
 * Printing the raw number with a "%" suffix showed 0.07%, and averaging the
 * two gave a book cap rate of 0.27%.
 */
export function capRatePercent(value: number | null | undefined): number | null {
  const n = positive(value)
  if (n == null) return null
  return n < 1 ? n * 100 : n
}

/** LTV as a fraction (0.65) whichever unit it was stored in, or null. */
export function ltvFraction(value: number | null | undefined): number | null {
  const n = positive(value)
  if (n == null) return null
  return n > 1 ? n / 100 : n
}

/**
 * A live loan in the shape the portfolio analytics were written for: amounts
 * in $MM, value and NOI on the loan, a market object and a triggers array.
 *
 * The analytics were built on the curated dataset and dereferenced fields no
 * live loan carries — `d.triggers.some(...)` threw on the first live loan and
 * took the whole app down with it (CRE-22). Missing inputs stay missing
 * (`undefined`); nothing is assumed in their place.
 */
export function analyticsLoan(loan: Loan): Loan {
  const value = recordedValueOf(loan)
  const noi = recordedNoiOf(loan)
  const market = (loan.market && typeof loan.market === 'object' ? loan.market : {}) as Partial<MarketData>
  return {
    ...loan,
    property: loan.property || loan.id,
    type: loan.type || ('Unclassified' as Loan['type']),
    location: typeof loan.location === 'string' ? loan.location : '',
    assignee: loan.assignee || 'Unassigned',
    loanAmount: toMillions(loan.loanAmount, amountUnitOf(loan)),
    propertyValue: value != null ? value / 1_000_000 : undefined,
    noi: noi != null ? noi / 1_000_000 : undefined,
    ltv: ltvFraction(loan.ltv) as number,
    dscr: positive(loan.dscr) as number,
    triggers: Array.isArray(loan.triggers) ? loan.triggers : [],
    market: { ...market, msa: market.msa ?? '' } as MarketData,
    cashFlows: {
      ...cashFlowsOf(loan),
      prospectiveNOI: noi != null ? noi / 1_000_000 : 0,
    },
  }
}

export interface AnalyticsBook {
  /** Funded loans, normalised by `analyticsLoan`. */
  funded: Loan[]
  /** Funded loans with value, NOI, LTV and DSCR all on record. */
  complete: Loan[]
  /** Funded loans the risk analyses leave out, and what each is missing. */
  incomplete: Array<{ id: string; property: string; missing: string[] }>
  /** Unfunded (pipeline) loans with LTV and DSCR on record, normalised. */
  pipeline: Loan[]
  /** Unfunded loans left out of the exposure view for want of LTV or DSCR. */
  pipelineExcluded: number
}

/**
 * The live portfolio for Portfolio Analytics: FUNDED loans only.
 *
 * The page treated every live loan — all in Triage — as the funded book. The
 * stress, LTV, VaR and risk analyses need value, NOI, LTV and DSCR; they used
 * to fill a missing value with loan ÷ 0.70 and a missing NOI with zero. A loan
 * without those inputs is now listed as excluded with the reason instead.
 */
export function analyticsBook(loans: Loan[]): AnalyticsBook {
  const funded = loans.filter(isFundedLoan).map(analyticsLoan)
  const unfunded = loans.filter((loan) => !isFundedLoan(loan)).map(analyticsLoan)
  const pipeline = unfunded.filter((deal) => deal.ltv != null && deal.dscr != null)
  const complete: Loan[] = []
  const incomplete: AnalyticsBook['incomplete'] = []
  for (const deal of funded) {
    const missing: string[] = []
    if (deal.propertyValue == null) missing.push('property value')
    if (deal.noi == null) missing.push('NOI')
    if (deal.ltv == null) missing.push('LTV')
    if (deal.dscr == null) missing.push('DSCR')
    if (missing.length === 0) complete.push(deal)
    else incomplete.push({ id: deal.id, property: deal.property, missing })
  }
  return { funded, complete, incomplete, pipeline, pipelineExcluded: unfunded.length - pipeline.length }
}

export interface StatementLine {
  label: string
  /** $MM. Deductions are positive amounts shown in parentheses. */
  amount: number
}

/**
 * The operating statement a live loan records, line by line, in $MM.
 *
 * Underwriting used to rebuild the statement from `rents` / `revenue` /
 * `expenses` keys the stored cash flows do not use (so every line was $0.00M
 * even on the fully populated control deal) and split total expenses 35/10/
 * 20/15/20 into taxes, insurance, management, repairs and other — an invented
 * allocation. This returns only lines the record holds; a total the record
 * does not state is derived from the lines and flagged as derived.
 */
export interface OperatingStatement {
  grossPotentialRent: number | null
  deductions: StatementLine[]
  otherIncome: number | null
  egi: number | null
  egiDerived: boolean
  expenses: StatementLine[]
  totalExpenses: number | null
  totalExpensesDerived: boolean
  noi: number | null
  occupancy: number | null
}

export function operatingStatementOf(loan: Loan): OperatingStatement {
  const cf = (loan.cashFlows && typeof loan.cashFlows === 'object' ? loan.cashFlows : {}) as unknown as Record<string, unknown>
  // Live cash flows are DOLLARS, every line of them. toMillions once guessed
  // the unit from the size of the number, so a $850 expense or a $600
  // bad-debt deduction below its 1,000 threshold printed as $850.00M.
  const mm = (dollars: number) => dollars / 1_000_000
  const m = (key: string): number | null => {
    const v = positive(cf[key])
    return v != null ? mm(v) : null
  }
  const grossPotentialRent = m('grossPotentialRent') ?? m('scheduledGrossIncome') ?? m('rents')
  const deductions: StatementLine[] = []
  for (const [label, key] of [
    ['Loss to Lease', 'lossToLease'],
    ['Vacancy Loss', 'vacancyLoss'],
    ['Concessions', 'concessions'],
    ['Bad Debt', 'badDebt'],
  ]) {
    const amount = m(key)
    if (amount != null) deductions.push({ label, amount })
  }
  const otherIncome = m('otherIncome')
  const statedEgi = m('effectiveGrossIncome') ?? m('revenue')
  const egi =
    statedEgi ??
    (grossPotentialRent != null
      ? grossPotentialRent - deductions.reduce((sum, d) => sum + d.amount, 0) + (otherIncome ?? 0)
      : null)

  const rawExpenses = cf.expenses
  const expenses: StatementLine[] = Array.isArray(rawExpenses)
    ? (rawExpenses as Array<Record<string, unknown>>)
        .map((line) => ({
          label: typeof line?.name === 'string' ? line.name : 'Expense',
          amount: positive(line?.amount) != null ? mm(line.amount as number) : 0,
        }))
        .filter((line) => line.amount > 0)
    : []
  const statedTotal = !Array.isArray(rawExpenses) ? m('expenses') : null
  const noiDollars = recordedNoiOf(loan)
  const noi = noiDollars != null ? mm(noiDollars) : null
  let totalExpenses: number | null = null
  let totalExpensesDerived = false
  if (expenses.length) {
    totalExpenses = expenses.reduce((sum, line) => sum + line.amount, 0)
  } else if (statedTotal != null) {
    totalExpenses = statedTotal
  } else if (egi != null && noi != null) {
    totalExpenses = egi - noi
    totalExpensesDerived = true
  }
  return {
    grossPotentialRent,
    deductions,
    otherIncome,
    egi,
    egiDerived: statedEgi == null && egi != null,
    expenses,
    totalExpenses,
    totalExpensesDerived,
    noi,
    occupancy: recordedOccupancyOf(loan),
  }
}

/**
 * Sources & Uses a live loan states, in $MM: the purchase price and total
 * project cost as the package gives them, the loan, and equity as stated or
 * as the difference. Underwriting used to show "Acquisition" = loan ÷ LTV
 * ($100.50M for Calloway) against a total of the loan itself ($68.00M).
 */
export interface StatedSourcesUses {
  purchasePrice: number | null
  totalCost: number | null
  /** The debt the Sources row carries: the FULL loan amount where the deal records one
   *  (`fully_funded_amount`, Backshop's Total Loan Amt — a construction deal's future funding is
   *  debt, not equity), else the Initial Loan Amount. Null when the deal states no loan amount
   *  (`loans.loan_amount` is NOT NULL and holds 0 for that — Cranbury, S02). */
  loan: number | null
  /** True where `loan` is the full loan amount rather than the Initial Loan Amount standing in. */
  loanIsFull: boolean
  equity: number | null
  equityDerived: boolean
}

export function statedSourcesUses(loan: Loan): StatedSourcesUses {
  const price = customNumber(loan, 'purchase_price')
  const cost = customNumber(loan, 'total_cost')
  const unit = amountUnitOf(loan)
  // 0 is the NOT NULL column's "no loan amount stated": no loan, so no
  // equity "derived" as the whole cost less a $0 loan.
  const initialM = loan.loanAmount > 0 ? toMillions(loan.loanAmount, unit) : null
  // LTC and Total Equity are on the FULL loan amount (Chris Young, confirmed 4 Nov 2026): the whole
  // commitment where the deal records one (0 is "none stated"), the Initial Loan Amount where it does not.
  const fullLoan = customNumber(loan, 'fully_funded_amount')
  const fullM = fullLoan != null && fullLoan > 0 ? toMillions(fullLoan, unit) : null
  const loanM = fullM ?? initialM
  const loanIsFull = fullM != null
  const statedEquity = customNumber(loan, 'total_equity')
  const purchasePrice = price != null ? toMillions(price, unit) : null
  const totalCost = cost != null ? toMillions(cost, unit) : null
  const total = totalCost ?? purchasePrice
  if (statedEquity != null) {
    return {
      purchasePrice, totalCost, loan: loanM, loanIsFull, equity: toMillions(statedEquity, unit), equityDerived: false,
    }
  }
  return {
    purchasePrice,
    totalCost,
    loan: loanM,
    loanIsFull,
    equity: total != null && loanM != null && total > loanM ? total - loanM : null,
    equityDerived: total != null && loanM != null && total > loanM,
  }
}

export function safeAverage(values: number[]): number {
  if (values.length === 0) return 0
  return values.reduce((sum, value) => sum + value, 0) / values.length
}

/**
 * A successful GET /loans is a list. Coerce unexpected wrappers so a
 * 200 with a non-array body becomes an empty live book — never a thrown
 * query that walls Underwriting / Surveillance.
 */
export function asLoanArray(data: unknown): Loan[] {
  if (Array.isArray(data)) return data as Loan[]
  if (data && typeof data === 'object') {
    const rec = data as Record<string, unknown>
    if (Array.isArray(rec.loans)) return rec.loans as Loan[]
    if (Array.isArray(rec.items)) return rec.items as Loan[]
  }
  return []
}
